import { toast } from 'react-toastify';
import { UPLOAD_MODE } from './fileUpload.constants';
import { uploadFile } from 'modules/shared/service';

/**
 * Trigger RHF validation after value change
 * @param {Function} trigger
 * @param {string} name
 */
export async function triggerValidation(trigger, name) {
  if (typeof trigger === 'function') {
    await trigger(name);
  }
}

/**
 * Resolve button label
 * @param {Object} params
 * @param {boolean} params.uploading
 * @param {boolean} params.hasValue
 * @param {string} params.buttonText
 * @returns {string}
 */
export function getButtonLabel({ uploading, hasValue, buttonText }) {
  if (uploading) return '';
  if (hasValue) return 'Re-upload';
  return buttonText;
}

/**
 * Handle file selection and upload flow
 * @param {Object} params
 * @param {File} params.file
 * @param {string} params.mode
 * @param {Function} params.onChange
 * @param {Function} params.trigger
 * @param {string} params.name
 * @param {Function} params.setUploading
 * @param {boolean} [props.showPreview=false]
 * @param {'before'|'after'} [props.previewPosition='after']
 * @param {string} [props.previewText='view']
 * @param {Object} [props.previewLinkProps] */
export async function handleFileChange({ file, mode, onChange, trigger, name, setUploading }) {
  if (!file) return;

  if (mode === UPLOAD_MODE.MANUAL) {
    onChange({ file });
    await triggerValidation(trigger, name);
    return;
  }

  try {
    setUploading(true);

    const uploaded = await uploadFile(file);

    onChange({
      id: uploaded.id,
      url: uploaded.url,
    });

    await triggerValidation(trigger, name);
  } catch (err) {
    toast.error(err?.message || 'File upload failed');
  } finally {
    setUploading(false);
  }
}

/**
 * Normalize file value into a predictable shape
 * @param {*} value
 * @returns {{ file: File|null, id: number|null, url: string|null }}
 */
export function normalizeFileValue(value) {
  if (!value) {
    return { file: null, id: null, url: null };
  }

  if (typeof value === 'number') {
    return { file: null, id: value, url: null };
  }

  if (typeof value !== 'object') {
    return { file: null, id: null, url: null };
  }

  return {
    file: value.file instanceof File ? value.file : null,
    id: typeof value.id === 'number' ? value.id : null,
    url: typeof value.url === 'string' ? value.url : null,
  };
}

/**
 * Check if value represents an uploaded file
 * @param {*} normalizedValue
 * @returns {boolean}
 */
export function hasUploadedFile(normalizedValue) {
  return Boolean(normalizedValue.id);
}
